import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Row,
  Table,
  Modal,
  Form,
  Container,
} from "react-bootstrap";
import { ShimmerTable } from "react-shimmer-effects";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import SparkApi from "../../api/SparkApi";
import InfoPill from "../InfoPill";
import Confirm from "../Confirm";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { usePermission } from "../context/PermissionContext";

const FactoryList = () => {
  const [factories, setFactories] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: null,
    status: "Active",
    address: null,
  });
  const { permissionData , userInfo } = usePermission();
  const can_create = permissionData?.Factory?.can_create;
  const can_edit = permissionData?.Factory?.can_edit;
  const can_delete = permissionData?.Factory?.can_delete;
  const [disableButton, setDisableButton] = useState(false);


  useEffect(() => {
    fetchFactoryData();
  }, []);

  async function fetchFactoryData() {
    try {
      const data = await SparkApi.fetchFactory();
      setFactories(data || []);
    } catch (error) {
      setFactories([]);
    }
  }
  const errorToastMessage = {
    width: "350px",
    color: "red",
  }
  const handleDeleteFactory = async () => {
    try {
      const result = await SparkApi.deleteFactoryRec(selectedFactory.id);
      if (result.success && result.message === "Factory Deleted Successfully") {
        setShowDeleteConfirm(false);
        fetchFactoryData();
        setSelectedFactory({});
        toast.success("Factory Deleted Successfully");
      } else {
        toast.error(result?.error , {style :errorToastMessage});
        console.error("Failed to delete factory");
      }
    } catch (error) {
      console.error("Error deleting factory:", error);
    }
  };

  const handleDelete = (row) => {
    setSelectedFactory(row);
    setShowDeleteConfirm(true);
  };

  const handleModalOpen = (factory = {}) => {
    if (factory.id) {
      setSelectedFactory(factory);
    }
    setFormData({
      name: factory.name || "",
      status: factory.status || "Active",
      address: factory.address || "",
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedFactory({});
    setFormData({
      name: "",
      status: "Active",
      address: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSaveFactory = async () => {
    try {
      if (
        !formData.name ||
        formData.name.trim() === "" ||
        !formData.status ||
        formData.name.trim() === ""
      ) {
        return toast.error("Missing required fields");
      }

      setDisableButton(true)

      if (selectedFactory.id) {
        const result = await SparkApi.updateFactoryRec(
          selectedFactory.id,
          formData
        );
        if (result.success) {
          toast.success("Factory Updated successfully");
          setDisableButton(false);
        } else {
          return toast.error(result.message);
        }
      } else {
        const result = await SparkApi.createFactoryRec(formData);
        if (result.success) {
          toast.success("Factory created successfully");
          setDisableButton(false);
        } else {
          return toast.error(result.message || "Failed to create factory");
        }
      }
      fetchFactoryData();
      handleModalClose();
    } catch (error) {
      console.error("Error saving factory:", error);
    }
  };

  const closeModal = () => {
    setSelectedFactory({});
    setShowDeleteConfirm(false);
  };

  const labels = {
    beforeSelect: " ",
  };

  const headers = [
    {
      title: "Name",
      prop: "name",
      isFilterable: true,
      isSortable:true,
      cell: (row) => <Link to={`/factories/${row.id}`}>{row.name}</Link>,
    },
    { 
      title: "Status",
      prop: "",
      isFilterable: true,
      isSortable:true ,
      cell : (row) => (
        <span 
          className={`badge px-3 py-2 text-white ${row?.status === 'Active' ? 'bg-success' : 'bg-danger'}`}
          style={{ minWidth: "80px", textAlign: "center" }}
        >
          {row?.status === 'Active' ? 'Active' : 'InActive'}
        </span>
      ),
    },
    { title: "Address", prop: "address", isFilterable: true, isSortable:true },
    (userInfo?.userrole === "ADMIN" || can_edit || can_delete) &&
    {
      title: "Actions",
      prop: "",
      cell: (row) => (
        <div className="factory-actions-btn">
          {(userInfo?.userrole === "ADMIN" || can_edit) && (
            <Button className="btn-sm mx-1" onClick={() => handleModalOpen(row)}>
              <i className="fa-regular fa-pen-to-square"></i>
            </Button>
          )}
          {(userInfo?.userrole === "ADMIN" || can_delete) && (
            <Button
              className="btn-sm mx-1"
              variant="danger"
              onClick={() => handleDelete(row)}
            >
              <i className="fa-regular fa-trash-can"></i>
            </Button>
          )}
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <Container>
      <Row className="g-0">
      <ToastContainer
          position="top-center"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Col lg={12}>
          {factories ? (
            <DatatableWrapper
              body={factories}
              headers={headers}
              paginationOptionsProps={{
                initialState: {
                  rowsPerPage: 10,
                  options: [5, 10, 15, 20],
                },
              }}
            >
              <Row className="mb-4">
                <Col xs={12} lg={4} sm={4} className="d-flex flex-col justify-content-end align-items-end">
                  <Filter />
                </Col>
                <Col xs={9} sm={6} lg={4} className="d-flex flex-col justify-content-start align-items-start"                >
                  <PaginationOptions labels={labels} />
                  <div className="mt-2">
                    <InfoPill
                      left="Total Factory"
                      right={factories ? factories.length : 0}
                    />
                  </div>
                </Col>
                <Col xs={3} lg={4} sm={2} className="d-flex flex-col justify-content-end align-items-end"                >
                  {
                    (userInfo?.userrole === "ADMIN" || can_create) && 
                    <Button
                      className="custom-btn-css-add-new-record btn-sm"
                      variant="outline-primary"
                      onClick={() => handleModalOpen()}
                    >
                      New Factory
                    </Button>
                  }
                </Col>
              </Row>
              <Table striped className="data-table factory-list" responsive>
                <TableHeader />
                <TableBody />
              </Table>
              <Pagination />
            </DatatableWrapper>
          ) : (
            <ShimmerTable row={10} col={8} />
          )}
        </Col>
        <Col lg={2}></Col>

        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton className="view-form-header">
            <Modal.Title>
              {selectedFactory.id ? "Edit Factory" : "New Factory"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Container className="view-form">
              <Row>
                <Col lg={12}>
                  <Form noValidate>
                    <Row>
                      <Col lg={6}>
                        <Form.Group className="mx-3">
                          <Form.Label className="form-view-label" htmlFor="formBasicName">
                            Name
                          </Form.Label>
                          <Form.Control
                            required
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter factory name"
                          />
                        </Form.Group>
                      </Col>
                      <Col lg={6}>
                        <Form.Group className="mx-3" controlId="formBasicSelect">
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            required
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            style={{ height: "36px" }}
                          >
                            <option value="" disabled>--Select status--</option>
                            <option value="Active" selected>
                              Active
                            </option>
                            <option value="Inactive">Inactive</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={12}>
                        <Form.Group className="mx-3" controlId="formBasicAddress">
                          <Form.Label
                            className="form-view-label"
                            htmlFor="formBasicAddress"
                          >
                            Address
                          </Form.Label>
                          <Form.Control
                            style={{ height: "36px" }}
                            as="textarea"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="Enter address"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary btn-sm" onClick={handleModalClose}>
              Close
            </Button>
            <Button disabled={disableButton} variant="primary btn-sm" onClick={handleSaveFactory}>
              {selectedFactory.id ? "Save Changes" : "Create Factory"}
            </Button>
          </Modal.Footer>
        </Modal>

        {showDeleteConfirm && (
          <Confirm
            show={showDeleteConfirm}
            handleCancel={closeModal}
            onHide={closeModal}
            handleDeleteFactory={handleDeleteFactory}
            title="Confirm delete?"
            message="You are going to delete the record. Are you sure?"
            table="factoryDelete"
          />
        )}
      </Row>
    </Container>
  );
};

export default FactoryList;
